import React, { useState } from "react";
import ERPModal from "../Components/ERPModal.js";
import { Helmet } from "react-helmet";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBook,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaBell,
  FaUserCircle,
  FaLayerGroup,
  FaTicketAlt,
  FaEnvelopeOpenText,
} from "react-icons/fa";
import { COLORS } from "../constants/colors";

const MobileAppPanel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    { title: "Student Dashboard", description: "Monitor student performance, assignments, and attendance in real-time.", icon: <FaUserGraduate /> },
    { title: "Teacher Dashboard", description: "Track teacher activities, classes, and performance metrics.", icon: <FaChalkboardTeacher /> },
    { title: "Assignments", description: "Create, assign, and manage homework and tasks easily.", icon: <FaBook /> },
    { title: "Attendance", description: "Keep accurate attendance records and view trends over time.", icon: <FaCalendarCheck /> },
    { title: "Fee Tracking", description: "Manage fee payments, dues, and financial records efficiently.", icon: <FaMoneyBillWave /> },
    { title: "Notifications", description: "Send alerts and updates instantly to students and staff.", icon: <FaBell /> },
    { title: "Profile Management", description: "Manage personal and account information securely.", icon: <FaUserCircle /> },
    { title: "Class Management", description: "Organize classes, sections, and student assignments.", icon: <FaLayerGroup /> },
    { title: "Support Tickets", description: "Track queries and support requests from students and staff.", icon: <FaTicketAlt /> },
  ];

  return (
    <>

    {/* SEO HEAD */}
<Helmet>
  {/* Primary SEO */}
  <title>
    School Mobile App for Students & Teachers | Gurukul ERP
  </title>

  <meta
    name="description"
    content="Gurukul ERP Mobile App helps schools manage attendance, homework, assignments, fees, notifications, communication, student progress, and teacher activities from one powerful mobile platform."
  />

  <meta
    name="keywords"
    content="
    school mobile app,
    school ERP mobile app,
    student mobile app,
    teacher mobile app,
    education mobile application,
    school management app,
    attendance tracking app,
    homework management app,
    online school app,
    mobile ERP software,
    student attendance app,
    teacher dashboard app,
    school communication app,
    assignment management system,
    school fee tracking app,
    cloud based school ERP,
    Gurukul ERP mobile app,
    education ERP software,
    student management app,
    teacher management app
    "
  />

  <meta name="author" content="Gurukul ERP" />

  <meta
    name="robots"
    content="index, follow"
  />

  <meta
    name="language"
    content="English"
  />

  <meta
    name="revisit-after"
    content="2 days"
  />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://gurukulerp.in/mobile-app"
  />

  {/* Open Graph SEO */}
  <meta
    property="og:title"
    content="School Mobile App for Students & Teachers | Gurukul ERP"
  />

  <meta
    property="og:description"
    content="Modern school mobile app for attendance, homework, assignments, fees, communication, notifications, and academic management."
  />

  <meta
    property="og:image"
    content="https://gurukulerp.in/assets/mobile-app-banner.jpg"
  />

  <meta
    property="og:url"
    content="https://gurukulerp.in/mobile-app"
  />

  <meta
    property="og:type"
    content="website"
  />

  <meta
    property="og:site_name"
    content="Gurukul ERP"
  />

  {/* Twitter SEO */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content="School Mobile App for Students & Teachers | Gurukul ERP"
  />

  <meta
    name="twitter:description"
    content="Powerful school mobile app for attendance, assignments, homework, communication, fee tracking, and academic management."
  />

  <meta
    name="twitter:image"
    content="https://gurukulerp.in/assets/mobile-app-banner.jpg"
  />

  {/* Mobile SEO */}
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1.0"
  />

  <meta
    name="theme-color"
    content="#3A4163"
  />

  {/* Geo SEO */}
  <meta
    name="geo.region"
    content="IN-HR"
  />

  <meta
    name="geo.placename"
    content="Panipat, Haryana"
  />

  {/* App SEO */}
  <meta
    name="apple-mobile-web-app-capable"
    content="yes"
  />

  <meta
    name="mobile-web-app-capable"
    content="yes"
  />

  <meta
    name="apple-mobile-web-app-status-bar-style"
    content="default"
  />

  {/* Structured Data */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context":
        "https://schema.org",

      "@type":
        "MobileApplication",

      name:
        "Gurukul ERP Mobile App",

      operatingSystem:
        "Android, iOS",

      applicationCategory:
        "EducationalApplication",

      url:
        "https://gurukulerp.in/mobile-app",

      image:
        "https://gurukulerp.in/assets/mobile-app-banner.jpg",

      description:
        "School mobile app for attendance, homework, assignments, communication, notifications, fees, and academic management for students and teachers.",

      brand: {
        "@type": "Brand",
        name: "Gurukul ERP",
      },

      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
      },

      creator: {
        "@type": "Organization",
        name: "Gurukul ERP",
      },
    })}
  </script>
</Helmet>

    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(to bottom, ${COLORS.background}, #ffffff)`,
      }}
    >
      {/* ================= HERO ================= */}
      <section className="relative py-28 text-center overflow-hidden">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
            One Smart App for{" "}
            <span style={{ color: COLORS.primary }}>
              Students & Teachers
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Manage attendance, homework, exams, fees, study materials,
            and communication — all in one seamless mobile experience.
          </p>

          <button
            onClick={() => setIsModalOpen(true)}
            className="text-white px-10 py-4 rounded-xl font-semibold shadow-lg hover:scale-105 transition duration-300"
            style={{ backgroundColor: COLORS.primary }}
          >
            Try Live Demo
          </button>
        </div>
      </section>

      {/* ================= ABOUT + VIDEO ================= */}
      <section className="flex flex-col lg:flex-row items-center py-20 px-8 max-w-7xl mx-auto">
        <div className="lg:w-1/2 flex justify-center mb-12 lg:mb-0">
          <div className="relative w-[260px] h-[520px] bg-black rounded-[40px] shadow-2xl p-3">
            <div className="w-full h-full bg-white rounded-[30px] overflow-hidden pt-4">
              <video
                src="/assets/mobileanim.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-3 bg-black rounded-full"></div>
          </div>
        </div>

        <div className="lg:w-1/2 lg:pl-16 text-center lg:text-left">
          <h2
            className="text-4xl font-bold mb-6"
            style={{ color: COLORS.textPrimary }}
          >
            About Gurukul Mobile App
          </h2>

          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            Gurukul Mobile App brings students and teachers together on
            a unified digital platform designed for efficiency and simplicity.
          </p>

          <ul className="space-y-3 text-gray-600 text-lg">
            <li>✔ Real-time academic tracking</li>
            <li>✔ Easy homework & assignment management</li>
            <li>✔ Instant communication & alerts</li>
            <li>✔ Secure access anywhere, anytime</li>
          </ul>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="py-24 bg-gradient-to-b from-[#f8fbff] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2
            className="text-4xl font-bold text-center mb-16"
            style={{ color: COLORS.textPrimary }}
          >
            ✨ Key Features
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group h-[200px] [perspective:1200px]">
                <div className="relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                  
                  {/* FRONT */}
                  <div
                    className="absolute inset-0 rounded-2xl shadow-lg flex flex-col items-center justify-center text-white text-center p-4 [backface-visibility:hidden] transition-all duration-500 group-hover:scale-105"
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                    }}
                  >
                    <div className="text-2xl mb-3">{feature.icon}</div>
                    <h3 className="text-md font-bold leading-snug">{feature.title}</h3>
                  </div>

                  {/* BACK */}
                  <div
                    className="absolute inset-0 rounded-2xl bg-white shadow-xl flex items-center justify-center text-center p-4 [transform:rotateY(180deg)] [backface-visibility:hidden]"
                    style={{ color: COLORS.textPrimary }}
                  >
                    <p className="text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-20 text-center bg-white">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-[#eef4ff] to-[#ffffff] p-12 rounded-3xl shadow-xl">
          <FaEnvelopeOpenText
            className="text-5xl mx-auto mb-6"
            style={{ color: COLORS.primary }}
          />

          <h2
            className="text-3xl font-bold mb-4"
            style={{ color: COLORS.textPrimary }}
          >
            Experience the Future of School Management
          </h2>

          <p className="text-gray-600 mb-8 text-lg">
            See how Gurukul Mobile App transforms communication, management, and learning.
          </p>

          <button
            onClick={() => setIsModalOpen(true)}
            className="text-white px-10 py-4 rounded-xl font-semibold shadow-lg hover:scale-105 transition duration-300"
            style={{ backgroundColor: COLORS.primary }}
          >
            Request Demo
          </button>
        </div>
      </section>

      <ERPModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
    </>
  );
};

export default MobileAppPanel;